import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  OPENING_MOVE_LINK_LABELS,
  type OpeningMovePreview,
  type OpeningMoveRequest,
  type OpeningResponse,
} from '@k-management/shared';
import { Link } from 'react-router';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';

/**
 * «Sposta la situazione» (2.13): the situation fixed at `from` moves to `to`.
 * Before confirming, the list of what is undone: the movements and spendings
 * come back free and proposed in the reconciliation, the notes to use.
 */
export function OpeningMoveDialog({
  from,
  to,
  onClose,
  onMoved,
}: {
  from: number;
  to: number;
  onClose: () => void;
  onMoved: (opening: OpeningResponse) => void;
}) {
  const queryClient = useQueryClient();
  const preview = useQuery({
    queryKey: ['treasury', 'ledgers', 'opening', from, 'move', to],
    queryFn: () =>
      apiRequest<OpeningMovePreview>(`/treasury/ledgers/${from}/opening/move?to=${to}`),
  });
  const move = useMutation({
    mutationFn: () => {
      const body: OpeningMoveRequest = { to };
      return apiRequest<OpeningResponse>(`/treasury/ledgers/${from}/opening/move`, {
        method: 'POST',
        body,
      });
    },
    onSuccess: async (opening) => {
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onMoved(opening);
    },
  });
  const data = preview.data;
  return (
    <ConfirmDialog
      title={`Sposta la situazione dal 01/01/${from} al 01/01/${to}`}
      confirmLabel={data ? `Sposta al 01/01/${to}` : 'Attendi…'}
      busy={!data || move.isPending}
      error={move.error?.message ?? preview.error?.message ?? null}
      onConfirm={() => move.mutate()}
      onCancel={onClose}
    >
      {!data ? (
        <p className="muted">Caricamento…</p>
      ) : (
        <div className="stack tight">
          <p>
            Le {data.items} partite al 01/01/{from} vengono tolte e si fissa la situazione al 01/01/
            {to}: {data.target.items} partite di {data.target.subjects} soggetti
            {data.target.toCheck > 0 &&
              `; ${data.target.toCheck} soggetti da controllare restano fuori`}
            .
          </p>
          {data.links.length === 0 ? (
            <p className="muted">Nessuna partita ha incassi, pagamenti o collegamenti.</p>
          ) : (
            <>
              <p>
                Prima si annullano questi {data.links.length} collegamenti, come si farebbe a mano.
                I movimenti e le spese tornano liberi e di nuovo proposti in Riconcilia.
              </p>
              <div className="table-wrap">
                <table className="data-table compact">
                  <thead>
                    <tr>
                      <th>Partita</th>
                      <th>Cosa si annulla</th>
                      <th className="numeric">Importo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.links.map((link, index) => (
                      <tr key={index}>
                        <td>
                          <Link to={`/tesoreria/fatture/${link.invoiceId}`}>{link.number}</Link>
                          <small className="muted block">{link.counterpartyName}</small>
                        </td>
                        <td>
                          {OPENING_MOVE_LINK_LABELS[link.kind]} del {formatDate(link.date)}
                          {link.detail && <small className="muted block">{link.detail}</small>}
                          {link.others > 0 && (
                            <small className="warn-text block">
                              libera anche {link.others}{' '}
                              {link.others === 1 ? 'altra fattura' : 'altre fatture'} dello stesso
                              movimento
                            </small>
                          )}
                        </td>
                        <td className="numeric">{formatMoney(link.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <p className="muted small">
            Tutto resta nel registro attività. Dopo, le fatture di prima del 01/01/{to} aperte a
            quella data sono partite della situazione: si chiudono con note di credito, incassi e
            pagamenti come le altre fatture.
          </p>
        </div>
      )}
    </ConfirmDialog>
  );
}
